import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Package, Plus, Minus, MoveRight, ArrowRight, MapPin, Check, ChevronLeft } from "lucide-react";
import { formatLocDisplay } from "./WarehouseExtras";

const LETTERS = "ABCDEFGHIJ";

function groupCabinetsByRow(cabinets) {
  const rows = {};
  cabinets.forEach(c => {
    const letter = c.cabinet.charAt(0);
    (rows[letter] ||= []).push(c);
  });
  Object.keys(rows).forEach(k => rows[k].sort((a, b) => a.cabinet.localeCompare(b.cabinet)));
  return Object.keys(rows).sort().map(letter => ({ letter, items: rows[letter] }));
}

// --- LocationPicker (Dollap -> Sirtar -> Pozicion) ---
export function LocationPicker({ initial, onSelect, currentProductId, blockOccupied = true }) {
  const [cabinet, setCabinet] = useState(initial?.cabinet || null);
  const [drawer, setDrawer] = useState(initial?.drawer || null);
  const [position, setPosition] = useState(initial?.position || null);
  const [cabinets, setCabinets] = useState([]);
  const [drawers, setDrawers] = useState([]);
  const [positions, setPositions] = useState([]);
  const [gridCfg, setGridCfg] = useState({ pos_rows: 5, pos_cols: 5 });

  useEffect(() => {
    api.get("/admin/warehouse/cabinets").then(r => setCabinets(r.data.cabinets));
  }, []);

  useEffect(() => {
    if (!cabinet) return;
    setDrawer(null); setPosition(null); setPositions([]);
    api.get(`/admin/warehouse/cabinet/${cabinet}/drawers`).then(r => {
      setDrawers(r.data.drawers);
      setGridCfg({ pos_rows: r.data.pos_rows || 5, pos_cols: r.data.pos_cols || 5 });
    });
  }, [cabinet]);

  useEffect(() => {
    if (!cabinet || !drawer) return;
    setPosition(null);
    api.get(`/admin/warehouse/drawer/${cabinet}/${drawer}`).then(r => {
      setPositions(r.data.positions);
      setGridCfg({ pos_rows: r.data.pos_rows || 5, pos_cols: r.data.pos_cols || 5 });
    });
  }, [cabinet, drawer]);

  useEffect(() => {
    if (cabinet && drawer && position) onSelect({ cabinet, drawer, position });
    else onSelect(null);
  }, [cabinet, drawer, position, onSelect]);

  const cabStatus = (c) => {
    if (c.occupied === 0) return "empty";
    if (c.occupied >= c.total) return "full";
    return "partial";
  };

  const posOccupiedByOther = (p) => p.occupied && p.product_id !== currentProductId;
  const freeCount = positions.filter(p => !p.occupied || p.product_id === currentProductId).length;
  const rows = groupCabinetsByRow(cabinets);
  const maxCols = Math.max(1, ...rows.map(r => r.items.length));

  return (
    <div className="grid md:grid-cols-3 gap-4">
      {/* Cabinet grid */}
      <div className="hg-card p-4">
        <div className="text-xs uppercase text-neutral-400 tracking-widest mb-3 flex items-center gap-2">
          <MapPin size={14} /> 1. Zgjidh dollapin
        </div>
        <div className="space-y-2">
          {rows.map(row => (
            <div key={row.letter} className="flex items-center gap-2">
              <div className="w-6 text-neutral-500 font-bold">{row.letter}</div>
              {row.items.map(c => {
                const id = c.cabinet;
                const st = cabStatus(c);
                const isSel = cabinet === id;
                const disabled = blockOccupied && st === "full";
                return (
                  <button
                    key={id}
                    disabled={disabled}
                    data-testid={`cab-${id}`}
                    onClick={() => setCabinet(id)}
                    className={`flex-1 min-w-0 h-10 text-xs font-bold border ${
                      isSel ? "bg-[#FFD600] text-black border-[#FFD600]"
                        : disabled ? "bg-neutral-800 text-neutral-600 border-neutral-800 cursor-not-allowed"
                        : st === "partial" ? "bg-[#FFD600]/20 text-[#FFD600] border-[#FFD600]/40 hover:bg-[#FFD600]/30"
                        : "bg-[#0f2f1a] text-emerald-400 border-emerald-800 hover:bg-emerald-900/40"
                    }`}
                  >
                    {id.slice(1)}
                  </button>
                );
              })}
              {Array.from({ length: Math.max(0, maxCols - row.items.length) }).map((_, i) => (
                <div key={`pad-${i}`} className="flex-1 min-w-0" />
              ))}
            </div>
          ))}
        </div>
        <div className="mt-3 flex items-center gap-3 text-[10px] text-neutral-400 flex-wrap">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-400"></span>I lirë</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#FFD600]"></span>Pjesërisht</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-neutral-600"></span>Plot</span>
        </div>
      </div>

      {/* Drawers */}
      <div className="hg-card p-4">
        <div className="text-xs uppercase text-neutral-400 tracking-widest mb-3 flex items-center gap-2">
          <Package size={14} /> 2. Zgjidh sirtarin {cabinet && <span className="text-[#FFD600]">({cabinet})</span>}
        </div>
        {!cabinet ? (
          <div className="text-neutral-500 text-sm py-8 text-center">Zgjidh dollapin fillimisht</div>
        ) : (
          <div className="grid grid-cols-4 gap-2">
            {drawers.map(rec => {
              const d = rec.drawer;
              const isFull = rec.occupied >= rec.capacity;
              const isSel = drawer === d;
              return (
                <button
                  key={d}
                  disabled={blockOccupied && isFull}
                  data-testid={`drawer-${d}`}
                  onClick={() => setDrawer(d)}
                  className={`h-10 text-xs font-bold border ${
                    isSel ? "bg-[#FFD600] text-black border-[#FFD600]"
                      : isFull ? "bg-neutral-800 text-neutral-600 border-neutral-800 cursor-not-allowed"
                      : rec.occupied > 0 ? "bg-[#FFD600]/15 text-[#FFD600] border-[#FFD600]/40 hover:bg-[#FFD600]/25"
                      : "bg-[#0f2f1a] text-emerald-400 border-emerald-800 hover:bg-emerald-900/40"
                  }`}
                >
                  {d}
                </button>
              );
            })}
          </div>
        )}
        {drawer && (
          <div className="mt-3 text-xs text-emerald-400" data-testid="drawer-free-count">
            {freeCount} vende të lira nga {positions.length}
          </div>
        )}
      </div>

      {/* Positions */}
      <div className="hg-card p-4">
        <div className="text-xs uppercase text-neutral-400 tracking-widest mb-3 flex items-center gap-2">
          <MapPin size={14} /> 3. Zgjidh pozicionin {cabinet && drawer && <span className="text-[#FFD600]">({cabinet}-{drawer})</span>}
        </div>
        {!drawer ? (
          <div className="text-neutral-500 text-sm py-8 text-center">Zgjidh sirtarin fillimisht</div>
        ) : (
          <div>
            <div className="grid gap-1 text-[10px] font-bold" style={{ gridTemplateColumns: `auto repeat(${gridCfg.pos_cols}, minmax(0, 1fr))` }}>
              <div></div>
              {Array.from({ length: gridCfg.pos_cols }, (_, i) => i + 1).map(c => <div key={c} className="text-neutral-500 text-center">{String(c).padStart(2, "0")}</div>)}
              {"ABCDEFGHIJ".slice(0, gridCfg.pos_rows).split("").map(r => (
                <div key={r} className="contents">
                  <div className="text-neutral-500 flex items-center justify-center pr-1">{r}</div>
                  {Array.from({ length: gridCfg.pos_cols }, (_, i) => i + 1).map(c => {
                    const id = `${r}-${String(c).padStart(2, "0")}`;
                    const p = positions.find(x => x.position === id) || { occupied: false };
                    const isMine = p.occupied && p.product_id === currentProductId;
                    const isOther = posOccupiedByOther(p);
                    const isSel = position === id;
                    const disabled = blockOccupied && isOther;
                    return (
                      <button
                        key={id}
                        disabled={disabled}
                        data-testid={`pos-${id}`}
                        onClick={() => setPosition(id)}
                        className={`aspect-square text-[10px] font-bold border ${
                          isSel ? "bg-[#FFD600] text-black border-[#FFD600]"
                            : isMine ? "bg-[#FFD600]/25 text-[#FFD600] border-[#FFD600]/50"
                            : isOther ? "bg-neutral-700 text-neutral-500 border-neutral-700 cursor-not-allowed"
                            : "bg-emerald-800/50 text-emerald-300 border-emerald-700 hover:bg-emerald-700/60"
                        }`}
                      >
                        {String(c).padStart(2, "0")}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
            <div className="mt-3 flex items-center gap-3 text-[10px] text-neutral-400 flex-wrap">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-500"></span>I lirë</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#FFD600]"></span>I zgjedhur</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-neutral-600"></span>I zënë</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// --- Modal wrapper ---
function Modal({ title, onClose, children, testId = "stock-modal" }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4" data-testid={testId}>
      <div className="absolute inset-0 bg-black/80" onClick={onClose} />
      <div className="relative bg-[#0B1220] border border-[#1F2B44] w-full max-w-5xl max-h-[95vh] overflow-y-auto">
        <div className="sticky top-0 bg-black/95 backdrop-blur-md border-b border-[#1F2B44] px-4 py-3 flex items-center justify-between z-10">
          <div className="font-display text-xl uppercase">{title}</div>
          <button onClick={onClose} data-testid="stock-modal-close"><X /></button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

// --- Place stock (VENDOS NË STOK) - 3 steps ---
export function PlaceStockModal({ productId, productName, onClose, onDone }) {
  const [step, setStep] = useState(1);
  const [qty, setQty] = useState(1);
  const [loc, setLoc] = useState(null);
  const [saving, setSaving] = useState(false);
  const onSelect = useCallback((v) => setLoc(v), []);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/products/${productId}/stock/set`, { qty, ...loc });
      toast.success(`+${qty} copë u vendosën në ${loc.cabinet}-${loc.drawer}-${loc.position}`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gabim");
    } finally { setSaving(false); }
  };

  return (
    <Modal title="Vendos në stok" onClose={onClose}>
      {/* Stepper */}
      <div className="flex items-center gap-2 mb-6">
        {[1, 2, 3].map(s => (
          <div key={s} className="flex items-center gap-2 flex-1">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${step >= s ? "bg-[#FFD600] text-black" : "bg-neutral-800 text-neutral-500"}`}>{s}</div>
            <div className="text-xs uppercase tracking-wider hidden sm:block">
              {s === 1 ? "Sasia" : s === 2 ? "Zgjidh vendin" : "Konfirmo"}
            </div>
            {s < 3 && <div className={`flex-1 h-px ${step > s ? "bg-[#FFD600]" : "bg-neutral-800"}`} />}
          </div>
        ))}
      </div>

      {step === 1 && (
        <div className="max-w-md">
          <label className="text-xs uppercase text-neutral-400 tracking-widest">Sa copë dëshiron të vendosësh?</label>
          <div className="flex items-center gap-3 mt-2">
            <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-12 h-12 border border-[#1F2B44] hover:bg-[#262626] flex items-center justify-center"><Minus /></button>
            <input type="number" value={qty} onChange={e => setQty(Math.max(1, +e.target.value))} data-testid="place-qty-input"
              className="w-32 h-12 bg-[#131E30] border border-[#1F2B44] text-center text-2xl font-bold" />
            <button onClick={() => setQty(qty + 1)} className="w-12 h-12 border border-[#1F2B44] hover:bg-[#262626] flex items-center justify-center"><Plus /></button>
            <span className="text-neutral-400">copë</span>
          </div>
        </div>
      )}

      {step === 2 && (
        <LocationPicker onSelect={onSelect} currentProductId={productId} blockOccupied={true} />
      )}

      {step === 3 && loc && (
        <div className="max-w-2xl">
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="hg-card p-4">
              <div className="text-xs uppercase text-neutral-400 tracking-widest mb-1">Produkti</div>
              <div className="font-semibold">{productName}</div>
            </div>
            <div className="hg-card p-4">
              <div className="text-xs uppercase text-neutral-400 tracking-widest mb-1">Sasia</div>
              <div className="font-display text-3xl text-[#FFD600]">{qty} copë</div>
            </div>
            <div className="hg-card p-4">
              <div className="text-xs uppercase text-neutral-400 tracking-widest mb-1">Vendi</div>
              <div className="font-mono text-lg">{loc.cabinet}-{loc.drawer}-{loc.position}</div>
            </div>
          </div>
          <button onClick={save} disabled={saving} data-testid="place-save"
            className="hg-btn-primary mt-6 h-14 px-8 uppercase text-sm inline-flex items-center gap-2">
            <Check size={18} /> {saving ? "Duke ruajtur..." : "Ruaj në stok"}
          </button>
        </div>
      )}

      <div className="mt-6 flex items-center justify-between border-t border-[#1F2B44] pt-4">
        {step > 1 ? (
          <button onClick={() => setStep(step - 1)} data-testid="place-back" className="border border-[#1F2B44] px-4 py-2 uppercase text-xs inline-flex items-center gap-1"><ChevronLeft size={14} /> Prapa</button>
        ) : <div />}
        {step < 3 && (
          <button
            data-testid="place-next"
            onClick={() => setStep(step + 1)}
            disabled={(step === 1 && qty < 1) || (step === 2 && !loc)}
            className="hg-btn-primary px-6 py-2 uppercase text-xs inline-flex items-center gap-1 disabled:opacity-40"
          >
            Vazhdo <ArrowRight size={14} />
          </button>
        )}
      </div>
    </Modal>
  );
}

// --- Add/Remove qty at an existing location ---
export function AdjustStockModal({ productId, mode, locations, onClose, onDone }) {
  // mode: "add" | "remove"
  const [locKey, setLocKey] = useState(locations[0] ? `${locations[0].cabinet}|${locations[0].drawer}|${locations[0].position}` : "");
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const isAdd = mode === "add";
  const title = isAdd ? "Shto stok" : "Nxirr nga stoku";
  const endpoint = isAdd ? "add" : "remove";

  const selected = locations.find(l => `${l.cabinet}|${l.drawer}|${l.position}` === locKey);

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.post(`/admin/products/${productId}/stock/${endpoint}`, {
        qty, cabinet: selected.cabinet, drawer: selected.drawer, position: selected.position, note,
      });
      toast.success(`${isAdd ? "+" : "−"}${qty} copë ${isAdd ? "u shtuan" : "u nxorrën"}`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gabim");
    } finally { setSaving(false); }
  };

  if (locations.length === 0) {
    return (
      <Modal title={title} onClose={onClose}>
        <div className="text-neutral-400">Ky produkt nuk ka lokacion në stok. Përdor "Vendos në stok" fillimisht.</div>
      </Modal>
    );
  }

  return (
    <Modal title={title} onClose={onClose}>
      <div className="grid gap-4 max-w-xl">
        {locations.length > 1 && (
          <div>
            <label className="text-xs uppercase text-neutral-400 tracking-widest">Zgjidh lokacionin</label>
            <select value={locKey} onChange={e => setLocKey(e.target.value)} data-testid="adjust-loc-select"
              className="mt-2 w-full h-12 bg-[#131E30] border border-[#1F2B44] px-3">
              {locations.map(l => (
                <option key={`${l.cabinet}|${l.drawer}|${l.position}`} value={`${l.cabinet}|${l.drawer}|${l.position}`}>
                  {l.cabinet}-{l.drawer}-{l.position} — {l.qty} copë
                </option>
              ))}
            </select>
          </div>
        )}
        {locations.length === 1 && selected && (
          <div className="hg-card p-3">
            <div className="text-xs uppercase text-neutral-400 tracking-widest">Lokacioni</div>
            <div className="font-mono">{selected.cabinet}-{selected.drawer}-{selected.position} • <span className="text-[#FFD600]">{selected.qty} copë</span></div>
          </div>
        )}
        <div>
          <label className="text-xs uppercase text-neutral-400 tracking-widest">{isAdd ? "Sa copë të shtohen?" : "Sa copë të nxjerrin?"}</label>
          <div className="flex items-center gap-3 mt-2">
            <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-12 h-12 border border-[#1F2B44] hover:bg-[#262626] flex items-center justify-center"><Minus /></button>
            <input type="number" value={qty} onChange={e => setQty(Math.max(1, +e.target.value))} data-testid="adjust-qty-input"
              className="w-28 h-12 bg-[#131E30] border border-[#1F2B44] text-center text-xl font-bold" />
            <button onClick={() => setQty(qty + 1)} className="w-12 h-12 border border-[#1F2B44] hover:bg-[#262626] flex items-center justify-center"><Plus /></button>
          </div>
          {selected && (
            <div className="text-sm text-neutral-400 mt-2">
              Pas veprimit: <span className="text-[#FFD600] font-bold">{isAdd ? selected.qty + qty : Math.max(0, selected.qty - qty)} copë</span>
            </div>
          )}
        </div>
        <div>
          <label className="text-xs uppercase text-neutral-400 tracking-widest">Shënim (opsional)</label>
          <input value={note} onChange={e => setNote(e.target.value)} data-testid="adjust-note"
            placeholder={isAdd ? "Faturë: 000123" : "Përdorim intern"}
            className="mt-2 w-full h-12 bg-[#131E30] border border-[#1F2B44] px-3" />
        </div>
        <button onClick={save} disabled={saving} data-testid="adjust-save"
          className="hg-btn-primary h-14 uppercase text-sm inline-flex items-center justify-center gap-2">
          {saving ? "Duke ruajtur..." : (isAdd ? "Shto në stok" : "Nxirr nga stoku")}
        </button>
      </div>
    </Modal>
  );
}

// --- Move stock ---
export function MoveStockModal({ productId, locations, onClose, onDone }) {
  const [fromKey, setFromKey] = useState(locations[0] ? `${locations[0].cabinet}|${locations[0].drawer}|${locations[0].position}` : "");
  const [toLoc, setToLoc] = useState(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const onSelect = useCallback((v) => setToLoc(v), []);
  const from = locations.find(l => `${l.cabinet}|${l.drawer}|${l.position}` === fromKey);

  const save = async () => {
    if (!from || !toLoc) return;
    setSaving(true);
    try {
      await api.post(`/admin/products/${productId}/stock/move`, {
        from_cabinet: from.cabinet, from_drawer: from.drawer, from_position: from.position,
        to_cabinet: toLoc.cabinet, to_drawer: toLoc.drawer, to_position: toLoc.position, note,
      });
      toast.success("U zhvendos me sukses");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gabim");
    } finally { setSaving(false); }
  };

  if (locations.length === 0) {
    return <Modal title="Zhvendos" onClose={onClose}><div className="text-neutral-400">Ky produkt nuk ka lokacion.</div></Modal>;
  }

  return (
    <Modal title="Zhvendos stokun" onClose={onClose}>
      <div className="grid gap-4">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase text-neutral-400 tracking-widest">Nga</label>
            {locations.length > 1 ? (
              <select value={fromKey} onChange={e => setFromKey(e.target.value)} data-testid="move-from-select"
                className="mt-2 w-full h-12 bg-[#131E30] border border-[#1F2B44] px-3">
                {locations.map(l => (
                  <option key={`${l.cabinet}|${l.drawer}|${l.position}`} value={`${l.cabinet}|${l.drawer}|${l.position}`}>
                    {l.cabinet}-{l.drawer}-{l.position} — {l.qty} copë
                  </option>
                ))}
              </select>
            ) : (
              <div className="mt-2 h-12 flex items-center px-3 bg-[#131E30] border border-[#1F2B44] font-mono">
                {from?.cabinet}-{from?.drawer}-{from?.position} — {from?.qty} copë
              </div>
            )}
          </div>
          <div>
            <label className="text-xs uppercase text-neutral-400 tracking-widest">Në</label>
            <div className="mt-2 h-12 flex items-center px-3 bg-[#131E30] border border-[#1F2B44] font-mono">
              {toLoc ? `${toLoc.cabinet}-${toLoc.drawer}-${toLoc.position}` : <span className="text-neutral-500 text-sm normal-case">Zgjidh nga harta poshtë</span>}
            </div>
          </div>
        </div>

        <div className="text-xs uppercase text-neutral-400 tracking-widest">Zgjidh vendin e ri</div>
        <LocationPicker onSelect={onSelect} currentProductId={productId} blockOccupied={true} />

        <div>
          <label className="text-xs uppercase text-neutral-400 tracking-widest">Shënim (opsional)</label>
          <input value={note} onChange={e => setNote(e.target.value)} data-testid="move-note"
            placeholder="Riorganizim..."
            className="mt-2 w-full h-12 bg-[#131E30] border border-[#1F2B44] px-3" />
        </div>

        <button onClick={save} disabled={saving || !toLoc} data-testid="move-save"
          className="hg-btn-primary h-14 uppercase text-sm inline-flex items-center justify-center gap-2 disabled:opacity-40">
          <MoveRight size={18} /> {saving ? "Duke zhvendosur..." : "Zhvendos tani"}
        </button>
      </div>
    </Modal>
  );
}
